// public/js/modules/firestore/firestoreCustomization.js

import { 
    getFirestore, 
    collection, 
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
    getDocs,
    collectionGroup,
    query,
    where,
    orderBy,
    deleteDoc,
    writeBatch,
    getDoc,
    setDoc,
    limit,
    arrayUnion
} from 'https://www.gstatic.com/firebasejs/9.23.0/firebase-firestore.js';
import { log } from '../logger.js';
import { generateTextColors } from '../ui/luminanceUtils.js';


export async function saveColorScheme(userId, schemeName, schemeData, schemeId = null) {
    const db = getFirestore();
    const schemesCol = collection(db, 'users', userId, 'colorSchemes');
    
    
    const colors = { ...schemeData.colors };
    colors.main = '#0a0a0a';

    const dataToSave = {
        name: schemeName,
        colors: colors,
        textColors: schemeData.textColors || generateTextColors(colors),
        fonts: schemeData.fonts || {},
        groups: schemeData.groups || [],
        bannerUrl: schemeData.bannerUrl || null,
        backgroundUrl: schemeData.backgroundUrl || null,
        displayOrder: schemeData.displayOrder || [],
        shadingLevel: schemeData.shadingLevel,
        gridOpacity: schemeData.gridOpacity !== undefined ? schemeData.gridOpacity : 3,
        createdBy: userId,
        createdByAdmin: schemeData.createdByAdmin !== undefined ? schemeData.createdByAdmin : false,
        isPublic: schemeData.isPublic !== undefined ? schemeData.isPublic : false,
        updatedAt: serverTimestamp(),
    };

    if (schemeId) {
        
        const schemeRef = doc(db, 'users', userId, 'colorSchemes', schemeId);
        await updateDoc(schemeRef, dataToSave);
        log.info(`Updated existing color scheme '${schemeName}' (ID: ${schemeId}) for user ${userId}.`);
        return schemeId;
    } else {
        dataToSave.createdAt = serverTimestamp();
        const docRef = await addDoc(schemesCol, dataToSave);
        log.info(`Saved new color scheme '${schemeName}' (ID: ${docRef.id}) for user ${userId}.`);
        
        
        const savedScheme = { id: docRef.id, ...dataToSave };
        await setThemeAsActive(userId, savedScheme);
        
        return docRef.id;
    }
}



export async function setThemeAsActive(userId, scheme) {
    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    log.info(`Setting theme '${scheme.name}' as active for user ${userId}.`);
    return updateDoc(userRef, {
        'preferences.activeScheme': scheme
    });
}



async function findSchemeByName(userId, schemeName) {
    const db = getFirestore();
    const schemesCol = collection(db, 'users', userId, 'colorSchemes');
    const q = query(schemesCol, where("name", "==", schemeName));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
        return null;
    }
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
}



export async function renameColorScheme(userId, oldName, newName) {
    const scheme = await findSchemeByName(userId, oldName);
    if (!scheme) {
        throw new Error(`Theme '${oldName}' not found.`);
    }
    const db = getFirestore();
    const schemeRef = doc(db, 'users', userId, 'colorSchemes', scheme.id);
    log.info(`Renaming theme '${oldName}' to '${newName}' for user ${userId}.`);
    return updateDoc(schemeRef, { name: newName });
}



export async function deleteColorSchemeByName(userId, schemeName) {
    const scheme = await findSchemeByName(userId, schemeName);
    if (!scheme) {
        throw new Error(`Theme '${schemeName}' not found.`);
    }
    return deleteColorScheme(userId, scheme.id);
}



export async function getUserColorSchemes(userId) {
    const db = getFirestore();
    const schemesCol = collection(db, 'users', userId, 'colorSchemes');
    const q = query(schemesCol, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    console.log(`[Firestore] getUserColorSchemes for ${userId} found ${snapshot.size} docs`);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}



export async function getPublicColorSchemes() {
    const db = getFirestore();
    const schemesGroup = collectionGroup(db, 'colorSchemes');
    const q = query(schemesGroup, where('isPublic', '==', true), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}



export async function setSchemePublicStatus(userId, schemeId, isPublic) {
    const db = getFirestore();
    const schemeRef = doc(db, 'users', userId, 'colorSchemes', schemeId);
    return updateDoc(schemeRef, { isPublic: isPublic });
}


export async function setDefaultColorScheme(schemeId, themeData = null) {
    const db = getFirestore();
    const batch = writeBatch(db);
    
    
    const stockThemesRef = collection(db, 'stockThemes');
    const q = query(stockThemesRef, where('isDefault', '==', true));
    const snapshot = await getDocs(q);
    
    snapshot.forEach(doc => {
        batch.update(doc.ref, { isDefault: false });
    });
    
    
    const newDefaultRef = doc(db, 'stockThemes', schemeId);
    const newDefaultSnap = await getDoc(newDefaultRef);
    
    if (!newDefaultSnap.exists()) {
        if (themeData) {
            log.info(`Theme '${schemeId}' not in stockThemes. Migrating/Adding now...`);
            const dataToSave = {
                name: themeData.name,
                colors: themeData.colors || {},
                textColors: themeData.textColors || generateTextColors(themeData.colors || {}),
                fonts: themeData.fonts || {},
                groups: themeData.groups || [],
                bannerUrl: themeData.bannerUrl || null,
                backgroundUrl: themeData.backgroundUrl || null,
                shadingLevel: themeData.shadingLevel,
                gridOpacity: themeData.gridOpacity !== undefined ? themeData.gridOpacity : 3,
                isDefault: true,
                createdAt: serverTimestamp(),
            };
            batch.set(newDefaultRef, dataToSave);
        } else {
            throw new Error(`Theme with ID '${schemeId}' not found in stockThemes collection.`);
        }
    } else {
        
        
        console.log('[Default Theme] Using existing theme colors from stockThemes');
        
        
        
        const colorsToSave = themeData && themeData.colors ? themeData.colors : newDefaultSnap.data().colors;
        
        console.log('[Default Theme] Colors to save:', colorsToSave);
        
        
        const backgroundUrl = (themeData && themeData.backgroundUrl) ? themeData.backgroundUrl : newDefaultSnap.data().backgroundUrl || null;
        const bannerUrl = (themeData && themeData.bannerUrl) ? themeData.bannerUrl : newDefaultSnap.data().bannerUrl || null;

        
        batch.update(newDefaultRef, { 
            isDefault: true,
            colors: colorsToSave,
            textColors: generateTextColors(colorsToSave),
            ...(backgroundUrl && { backgroundUrl }),
            ...(bannerUrl && { bannerUrl })
        });
    }
    
    
    const configRef = doc(db, 'config', 'app_preferences');
    batch.set(configRef, { defaultColorSchemeId: schemeId }, { merge: true });
    
    return batch.commit();
}


export async function getDefaultColorScheme() {
    try {
        const db = getFirestore();
        
        
        
        const stockThemesRef = collection(db, 'stockThemes');
        const q = query(stockThemesRef, where('isDefault', '==', true), limit(1));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            console.log('[Firestore] getDefaultColorScheme: Found via isDefault flag:', doc.data().name);
            return { id: doc.id, ...doc.data() };
        }
        
        
        console.log('[Firestore] getDefaultColorScheme: No isDefault flag, checking config document...');
        const configRef = doc(db, 'config', 'app_preferences');
        const configSnap = await getDoc(configRef);
        
        if (configSnap.exists() && configSnap.data().defaultColorSchemeId) {
            const schemeId = configSnap.data().defaultColorSchemeId;
            const schemeRef = doc(db, 'stockThemes', schemeId);
            const schemeSnap = await getDoc(schemeRef);
            if (schemeSnap.exists()) {
                console.log('[Firestore] getDefaultColorScheme: Found via config:', schemeSnap.data().name);
                return { id: schemeSnap.id, ...schemeSnap.data() };
            }
        }
        
        return null;
    } catch (error) {
        
        if (error.code === 'permission-denied') {
            console.log('[Firestore] getDefaultColorScheme: Permission denied, returning null');
            return null;
        }
        throw error;
    }
}


export async function deleteColorScheme(userId, schemeId) {
    const db = getFirestore();
    const schemeRef = doc(db, 'users', userId, 'colorSchemes', schemeId);
    log.info(`Deleting theme with ID '${schemeId}' for user ${userId}.`);
    return deleteDoc(schemeRef);
}


export async function copyThemeToUser(userId, themeData) {
    const db = getFirestore();
    const schemesCol = collection(db, 'users', userId, 'colorSchemes');
    const dataToSave = {
        name: themeData.name,
        colors: themeData.colors || {},
        textColors: themeData.textColors || generateTextColors(themeData.colors || {}),
        fonts: themeData.fonts || {},
        groups: themeData.groups || [],
        bannerUrl: themeData.bannerUrl || null,
        backgroundUrl: themeData.backgroundUrl || null,
        displayOrder: themeData.displayOrder || [],
        shadingLevel: themeData.shadingLevel,
        gridOpacity: themeData.gridOpacity !== undefined ? themeData.gridOpacity : 3,
        createdBy: userId,
        originalCreatedBy: themeData.createdBy || themeData.originalCreatedBy,
        isPublic: false,
        isBorrowed: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(schemesCol, dataToSave);
    log.info(`Copied theme '${themeData.name}' to user ${userId} as private (ID: ${docRef.id}).`);
    return docRef.id;
}





export async function adminAddStockTheme(themeName, themeData, customId = null) {
    const db = getFirestore();
    const stockThemesCol = collection(db, 'stockThemes');
    const dataToSave = {
        name: themeName,
        colors: themeData.colors || {},
        textColors: themeData.textColors || generateTextColors(themeData.colors || {}),
        fonts: themeData.fonts || {},
        groups: themeData.groups || [],
        bannerUrl: themeData.bannerUrl || null,
        backgroundUrl: themeData.backgroundUrl || null,
        shadingLevel: themeData.shadingLevel,
        gridOpacity: themeData.gridOpacity !== undefined ? themeData.gridOpacity : 3,
        createdByAdmin: true,
        createdAt: serverTimestamp(),
    };

    
    if (themeData.createdBy) {
        dataToSave.createdBy = themeData.createdBy;
    }
    
    if (themeData.originalCreatedBy) {
        dataToSave.originalCreatedBy = themeData.originalCreatedBy;
    }

    if (customId) {
        const docRef = doc(db, 'stockThemes', customId);
        
        delete dataToSave.createdAt;
        dataToSave.updatedAt = serverTimestamp();
        
        await setDoc(docRef, dataToSave, { merge: true });
        log.info(`Admin updated stock theme with custom ID: '${themeName}' (${customId}).`);
        return customId;
    } else {
        const docRef = await addDoc(stockThemesCol, dataToSave);
        log.info(`Admin added new stock theme: '${themeName}'.`);
        return docRef.id;
    }
}



export async function adminRemoveStockTheme(themeName) {
    const db = getFirestore();
    const stockThemesCol = collection(db, 'stockThemes');
    const q = query(stockThemesCol, where("name", "==", themeName));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
        throw new Error(`Stock theme '${themeName}' not found.`);
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
    });
    await batch.commit();
    log.info(`Admin removed stock theme: '${themeName}'.`);
}